import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined, normalizeFileName } from "../../../../../../common/utils/utils";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { EntregaObraClienteArquivoModel } from "../../../../domain/models/entrega_obra_cliente_arquivo";
import { EntregaObraClienteArquivoForm, EntregaObraClienteArquivoSchema } from "../../../schemas/entrega_obra_cliente_lista/entregaObraClienteArquivoCadastro";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

/**
 * CadastroDocumentoModal
 */

type Props = {
    open: boolean,
    saving: boolean,
    selectedEntregaObraArquivo: EntregaObraClienteArquivoModel | null,
    idEntregaObra: number,

    onSubmit: (form: EntregaObraClienteArquivoForm) => void,
    onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
    onDownload: (idEntregaObraClienteArquivo: number) => void,
    onClose: () => void,
};

export const EntregaObraClienteArquivoModal = ({
    open,
    saving,
    selectedEntregaObraArquivo,
    idEntregaObra,

    onSubmit,
    onOpenChangeHistory,
    onDownload,
    onClose,
}: Props) => {
    const [entregaObraArquivo, setEntregaObraArquivo] = useState(selectedEntregaObraArquivo);

    const hookForm = useForm<EntregaObraClienteArquivoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(EntregaObraClienteArquivoSchema),
    });

    useEffect(() => {
        if (open) {
            if (selectedEntregaObraArquivo) {
                hookForm.setValue('idEntregaObra', idEntregaObra);
                hookForm.setValue('idEntregaObraClienteArquivo', selectedEntregaObraArquivo.idEntregaObraClienteArquivo);
                hookForm.setValue('descricao', selectedEntregaObraArquivo.descricao);
                hookForm.setValue('nome', selectedEntregaObraArquivo.nome);
                hookForm.setValue('arquivo', null);
                hookForm.setValue('limparArquivo', false);
            } else {
                hookForm.reset();
                hookForm.setValue('idEntregaObra', idEntregaObra);
            }
            setEntregaObraArquivo(selectedEntregaObraArquivo ?? null);
        }
    }, [
        idEntregaObra,
        open,
        selectedEntregaObraArquivo,
        hookForm,
    ]);

    const handleClose = useCallback(() => {
        if (!saving) onClose();
    }, [
        saving,
        onClose,
    ]);

    const handleDownload = useCallback(() => {
        if (!saving && entregaObraArquivo)
            onDownload(entregaObraArquivo.idEntregaObraClienteArquivo);
    }, [
        saving,
        entregaObraArquivo,
        onDownload,
    ]);

    const handleDeleteFile = useCallback(() => {
        if (!saving) {
            if (window.confirm('Você deseja excluir este item?')) {
                hookForm.setValue('limparArquivo', true);
                hookForm.setValue('nome', null);
                setEntregaObraArquivo(null);
            }
        }
    }, [
        saving,
        hookForm,
    ]);

    const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
        if (!saving && e.target.files && e.target.files.length > 0) {
            hookForm.setValue('limparArquivo', false);
            hookForm.setValue('nome', e.target.files.item(0)?.name ?? null);
            onChange(e.target.files.item(0));
        }
    }, [
        saving,
        hookForm,
    ]);

    const handleSubmit = useCallback((form: EntregaObraClienteArquivoForm) => {
        if (!saving) onSubmit(form);
    }, [
        saving,
        onSubmit,
    ]);

    const handleChangeHistory = useCallback(() => {
        if (selectedEntregaObraArquivo) {
            onOpenChangeHistory(selectedEntregaObraArquivo.idEntregaObraClienteArquivo, LogTipoTabela.ENTREGA_OBRA_CLIENTE_ARQUIVO);
        }
    }, [selectedEntregaObraArquivo, onOpenChangeHistory]);

    return (
        <Dialog
            open={open}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit),
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "35rem",
                    },
                },
            }}>
            <DialogTitle>
                Cadastrar documento
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Cadastro Arquivo"} arrangement="fullRow">
                        <>

                            <DefaultFormInput
                                name={'descricao'}
                                label={"Nome do Arquivo"}
                                readOnly={saving}
                                required={true}
                                reactHookForm={hookForm}
                            />

                            <div className="flex w-full">
                                <Controller
                                    name='arquivo'
                                    control={hookForm.control}
                                    render={({ field }) => (entregaObraArquivo && entregaObraArquivo.nome && entregaObraArquivo.nome.length > 0) ? (
                                        <div className="w-full flex flex-row justify-between items-center">
                                            <span
                                                className="cursor-pointer"
                                                onMouseDown={handleDownload}>
                                                {entregaObraArquivo.nome && entregaObraArquivo.nome.length > 40
                                                    ? `${entregaObraArquivo.nome.substring(0, 40)}...`
                                                    : entregaObraArquivo.nome}
                                            </span>
                                            <IconButton onClick={handleDeleteFile}>
                                                <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                                            </IconButton>
                                        </div>
                                    ) : (
                                        <PrimaryButton
                                            component="label"
                                            role={undefined}
                                            variant="contained"
                                            tabIndex={-1}
                                            disabled={saving}
                                            startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                                            {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                                            <VisuallyHiddenInput
                                                type="file"
                                                onChange={(e) => handleFileChange(e, field.onChange)}
                                                multiple={false} />
                                        </PrimaryButton>
                                    )} />
                            </div>
                        </>
                    </FormContainer>
                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full h-full items-center justify-between flex p-4 gap-4">
                    <div>
                        {
                            isNotNullOrUndefined(selectedEntregaObraArquivo) &&
                            <PrimaryButton
                                buttonColor="yellow"
                                onClick={handleChangeHistory}>
                                <span className="text-xs">
                                    Historico de alterações
                                </span>
                            </PrimaryButton>
                        }
                    </div>

                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog>
    );
}